import { makeTransition } from 'src/utils/motion/transitions';
import { AnimatePresence, motion } from 'framer-motion';
import { StyleProps } from '@summer/jst-react';
import styled, { css } from 'styled-components';
import { Button, CleanButton } from 'src/components/common/Button';
import { transparentize } from 'polished';
import { ChevronRight } from 'src/components/icons/ChevronRight';
import { ChevronLeft } from 'src/components/icons/ChevronLeft';
import { ScrollableContent } from 'src/components/ScrollableContent';
import { useTranslate } from 'react-t7e';
import { memo } from 'react';
import { casePathEmptyString } from 'src/utils/casePathEmptyString';
import { media } from 'src/styles/designs';

export type VerticalMenuOption<T extends string> = {
  group: string;
  label: string;
  value: T;
  subitems: { label: string; value: string }[];
};

export interface VerticalMenuProps<T extends string> {
  options: VerticalMenuOption<T>[];
  activeOption: T | undefined;
  activeSubItem?: string;
  onSubItemClick?: (v: string) => void;
  onSubItemHover?: (v: string | undefined) => void;
  onOptionClick?: (v: T) => void;
  onOptionHover?: (v: T | undefined) => void;
  resetActiveOption?: () => void;
}

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  padding-top: 30px;
  height: 250px;

  ${media.w.greaterThanOrEqual.desktop} {
    max-width: 275px;
  }
`;

const Content = styled(ScrollableContent)`
  padding-right: 10px;
  height: 220px;
`;

const BackContainer = styled(motion.div)`
  position: absolute;
  top: 0;
`;

const TabButton = styled(motion(CleanButton))`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  width: 100%;
  transition: 0.25s;

  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  text-transform: uppercase;

  border-bottom: 1rpx solid
    ${({ theme }) => transparentize(0.4, theme.text.palette.primary)};
  svg {
    transition: 0.25s;
    fill: ${({ theme }) => transparentize(0.4, theme.text.palette.primary)};
  }

  &:hover {
    border-color: currentColor;
    svg {
      fill: currentColor;
    }
  }
`;

const StyledTabButton = styled(TabButton)<{ $active?: boolean }>`
  text-transform: initial;
  font-weight: 300;

  ${casePathEmptyString('$active')(css`
    font-weight: 700;
    border-color: currentColor;
  `)}
`;

const VerticalMenuComponent = <T extends string = string>({
  options,
  activeOption,
  activeSubItem,
  onSubItemClick,
  onSubItemHover,
  onOptionClick,
  onOptionHover,
  resetActiveOption,
  ...props
}: VerticalMenuProps<T> & StyleProps) => {
  const { _ } = useTranslate();

  const active = options.find(
    (option) =>
      option.value === activeOption ||
      option.subitems.find((s) => s.value === activeSubItem),
  );

  return (
    <Container {...props}>
      <AnimatePresence initial={false} exitBeforeEnter>
        {active && (
          <BackContainer
            key="back"
            {...makeTransition({
              fade: true,
              from: 'bottomMore',
              to: 'none',
              enterDelay: 0.5,
            })}
          >
            <Button
              kind="rounded"
              size="small"
              icon={ChevronLeft}
              onClick={() => resetActiveOption?.()}
            >
              {_(active.group)}
            </Button>
          </BackContainer>
        )}
      </AnimatePresence>

      <Content>
        <AnimatePresence initial={false} exitBeforeEnter>
          {active ? (
            <motion.div
              key="active"
              {...makeTransition({
                fade: true,
                stagger: active.subitems.length > 5 ? 0.02 : 0.1,
              })}
            >
              {active.subitems.map((item) => (
                <StyledTabButton
                  key={item.value}
                  onMouseEnter={() => onSubItemHover?.(item.value)}
                  onMouseLeave={() => onSubItemHover?.(undefined)}
                  onClick={() => {
                    onSubItemClick?.(item.value);
                    onSubItemHover?.(undefined);
                  }}
                  $active={activeSubItem === item.value}
                  {...makeTransition({
                    fade: true,
                    forStagger: true,
                    from: 'top',
                  })}
                >
                  {_(item.label)}
                </StyledTabButton>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="default"
              {...makeTransition({ fade: true, stagger: 0.1 })}
            >
              {options.map((option) => (
                <TabButton
                  key={option.value}
                  onMouseEnter={() => onOptionHover?.(option.value)}
                  onMouseLeave={() => onOptionHover?.(undefined)}
                  onClick={() => {
                    onOptionClick?.(option.value);
                    onOptionHover?.(undefined);
                  }}
                  {...makeTransition({
                    fade: true,
                    forStagger: true,
                    from: 'top',
                  })}
                >
                  {_(option.label)} <ChevronRight size={16} />
                </TabButton>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </Content>
    </Container>
  );
};

export const VerticalMenu = memo(
  VerticalMenuComponent,
) as typeof VerticalMenuComponent;
